import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Section.module.css';

export interface SectionProps {
  /**
   * Main section heading
   */
  title?: string;

  /**
   * Subtitle/eyebrow text above title
   */
  subtitle?: string;

  /**
   * Optional description below title
   */
  description?: string;

  /**
   * Section content
   */
  children: ReactNode;

  /**
   * Background variant
   * @default 'default'
   */
  background?: 'default' | 'subtle' | 'primary' | 'secondary';

  /**
   * Center align header content
   * @default false
   */
  centered?: boolean;

  /**
   * Section size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * HTML element to render as
   * @default 'section'
   */
  as?: 'section' | 'div' | 'article';

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Section Component
 *
 * A flexible content section with optional header (title, subtitle, description).
 * Used to organize page content into distinct, well-spaced areas.
 *
 * @example
 * ```tsx
 * <Section
 *   title="Features"
 *   subtitle="What we offer"
 *   centered
 * >
 *   <div>Section content here</div>
 * </Section>
 * ```
 */
export const Section = ({
  title,
  subtitle,
  description,
  children,
  background = 'default',
  centered = false,
  size = 'md',
  as: Component = 'section',
  className
}: SectionProps) => {
  const hasHeader = title || subtitle || description;

  return (
    <Component
      className={clsx(
        styles.section,
        styles[`bg${background.charAt(0).toUpperCase()}${background.slice(1)}`],
        styles[`size-${size}`],
        centered && styles.centered,
        className
      )}
    >
      <div className={styles.container}>
        {hasHeader && (
          <header className={styles.header}>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            {title && <h2 className={styles.title}>{title}</h2>}
            {description && <p className={styles.description}>{description}</p>}
          </header>
        )}

        <div className={styles.content}>{children}</div>
      </div>
    </Component>
  );
};
